/**
 * New User Detection
 *
 * Pure functions for deciding whether to create a new document
 * or show an error when no localStorage URL exists.
 *
 * These are extracted from AutomergeProvider for testability.
 */

export type NewUserDecision =
  | { action: 'create-document' }
  | { action: 'show-error'; reason: string }

/**
 * Determine whether it's safe to create a new Automerge document.
 *
 * Only creates a document when:
 * 1. No URL in localStorage (no local document reference)
 * 2. Profile was successfully queried AND returned no URL (confirmed new user)
 *
 * Returns 'show-error' when the profile couldn't be checked (network/auth failure),
 * because the user might have an existing document on the profile that we can't see.
 * Creating a new doc would overwrite it.
 */
export function decideNewUserAction(
  savedDocUrl: string | null,
  profileChecked: boolean,
): NewUserDecision | null {
  // Has a URL — not a new user scenario
  if (savedDocUrl) return null

  if (!profileChecked) {
    return {
      action: 'show-error',
      reason:
        'Profile check failed — cannot verify this is a new user. Existing data may exist.',
    }
  }

  return { action: 'create-document' }
}
